import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { nowBrazil } from '@/lib/timezone'
import type { Client } from '@/types'

const QK = ['clients'] as const

export function useClients() {
  const qc = useQueryClient()

  const { data: clients = [], isPending, isError, refetch } = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const { data, error } = await supabase.from('clients').select('*').order('legal_name').limit(500)
      if (error) throw error
      return (data || []) as Client[]
    },
  })

  const createM = useMutation({
    mutationFn: async (client: Partial<Client>) => {
      const { data, error } = await supabase.from('clients').insert(client).select().single()
      if (error) throw error
      return data as Client
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Cliente criado') },
    onError: () => { toast.error('Erro ao criar cliente') },
  })

  const updateM = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Client> }) => {
      const { data, error } = await supabase.from('clients').update({ ...updates, updated_at: nowBrazil() }).eq('id', id).select().single()
      if (error) throw error
      return data as Client
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Cliente atualizado') },
    onError: () => { toast.error('Erro ao atualizar cliente') },
  })

  const removeM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('clients').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: QK }); toast.success('Cliente removido') },
    onError: () => { toast.error('Erro ao remover cliente') },
  })

  const create = async (client: Partial<Client>) => {
    try { const data = await createM.mutateAsync(client); return { data, error: null } }
    catch (error) { return { data: null, error } }
  }
  const update = async (id: string, updates: Partial<Client>) => {
    try { const data = await updateM.mutateAsync({ id, updates }); return { data, error: null } }
    catch (error) { return { data: null, error } }
  }
  const remove = async (id: string) => {
    try { await removeM.mutateAsync(id); return { error: null } }
    catch (error) { return { error } }
  }

  return { clients, loading: isPending, isError, refetch, create, update, remove }
}
